import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import "./CantidadesGeneradas.scss";
import InputDropdown from "../../../components/inputs/InputDropdown/InputDropdown";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import Table from "../../../components/common/Table/Table";
import CardStats from "../../../components/common/CardStats/CardStats";
import { HiTrendingUp } from "react-icons/hi";
import { MdRecycling, MdDelete, MdFilterList } from "react-icons/md";
import ScrollableBarChartGraph from "../../../components/common/Charts/ScrollableBarChartGraph";
import Modal from "../../../components/common/Modal/Modal";
import { useZone } from "../../../context/ZoneContext";
import { MdOutlineFileDownload } from "react-icons/md";

const columnasResiduos = [
    { key: "residuo", label: "Residuo", visible: true, sortable: true },
    {
        key: "pesoTotal",
        label: "Peso Total (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => new Intl.NumberFormat("es-ES").format(row.pesoTotal),
    },
    {
        key: "cantidadValorizada",
        label: "Valorización (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES").format(row.cantidadValorizada),
    },
    {
        key: "cantidadEliminada",
        label: "Eliminación (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES").format(row.cantidadEliminada),
    },
    {
        key: "volumenTotal",
        label: "Volumen (m³)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            `${new Intl.NumberFormat("es-ES", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(row.volumenTotal)} m³`,
    },
    {
        key: "porcentajeValorizacion",
        label: "% Valorización",
        visible: true,
        sortable: false,
        type: "number",
        render: (row) => {
            const percentage =
                row.pesoTotal > 0
                    ? (row.cantidadValorizada / row.pesoTotal) * 100
                    : 0;
            return `${percentage.toFixed(1)}%`;
        },
    },
];

const meses = [
            { value: "all", label: "Todos los meses" },
            ...Array.from({ length: 12 }, (_, i) => ({
                value: i + 1,
                label: [
                    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
                ][i],
            })),
        ];

export default function CantidadesGeneradas() {
    const [activeTab, setActiveTab] = useState("grafica");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalExcelOpen, setIsModalExcelOpen] = useState(false);
    const { selectedZone } = useZone();

    const [informeCantidadesGeneradas, setInformeCantidadesGeneradas] =
        useState([]);

    const [selectedProyecto, setSelectedProyecto] = useState("all");
    const [selectedFecha, setSelectedFecha] = useState("all");
    const [selectedMes, setSelectedMes] = useState("all");
    const [selectedCodigo, setSelectedCodigo] = useState("all");
    const [selectedResiduos, setSelectedResiduos] = useState([]);
    const [selectedTransportista, setSelectedTransportista] = useState("all");
    const [selectedGestorFinal, setSelectedGestorFinal] = useState("all");
    const [selectedAnos, setSelectedAnos] = useState("all");

    const getYearFromFechaResiduo = (item) => {
        const fecha = item.fechaResiduo;

        if (!fecha) return null;

        const year = new Date(fecha).getFullYear();

        return year > 1950 ? year : null;
    };

    useEffect(() => {
        const fetchInformeCantidadesGeneradas = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(
                    `/api/Informe/GetCantidadesGeneradas`
                );
                setInformeCantidadesGeneradas(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeCantidadesGeneradas();
    }, []);

    const datosNormalizados = useMemo(() => {
        return informeCantidadesGeneradas.map((item) => {
            const fecha = item.fechaResiduo ? new Date(item.fechaResiduo) : null;
            const anioResiduo = getYearFromFechaResiduo(item);

            return {
                ...item,
                proyectoFinal: item.proyecto || item.nombreObra,
                anioResiduo,
                mesResiduo:
                    fecha && !Number.isNaN(fecha.getTime())
                        ? fecha.getMonth() + 1
                        : null,
            };
        });
    }, [informeCantidadesGeneradas]);

    const {
        proyectos,
        fechasFin,
        codigosObra,
        residuosDisponibles,
        transportistas,
        gestoresFinales,
    } = useMemo(() => {
        if (datosNormalizados.length === 0) {
            return {
                proyectos: [],
                fechasFin: [],
                codigosObra: [],
                meses: [],
                residuosDisponibles: [],
                transportistas: [],
                gestoresFinales: [],
            };
        }

        const proyectosUnicos = [
            ...new Set(
                datosNormalizados.map(
                    (item) => item.proyecto || item.nombreObra
                )
            ),
        ];

        const aniosUnicos = [
            ...new Set(
                datosNormalizados
                    .map((item) => item.anioResiduo)
                    .filter(Boolean)
            ),
        ].sort((a, b) => b - a);

        console.log("Años únicos extraídos:", aniosUnicos);

        const codigosUnicos = [
            ...new Set(datosNormalizados.map((item) => item.codObra)),
        ];

        const residuosUnicos = [
            ...new Set(datosNormalizados.map((item) => item.residuo)),
        ].filter(Boolean);

        // 🔥 NUEVOS
        const transportistasUnicos = [
            ...new Set(datosNormalizados.map((item) => item.transportista)),
        ].filter(Boolean);

        const gestoresFinalesUnicos = [
            ...new Set(datosNormalizados.map((item) => item.gestorFinal)),
        ].filter(Boolean);

        return {
            proyectos: [
                { value: "all", label: "Todos los Proyectos" },
                ...proyectosUnicos.map((p) => ({ value: p, label: p })),
            ],
            fechasFin: [
                { value: "all", label: "Todos los años" },
                ...aniosUnicos.map((a) => ({
                    value: a,
                    label: a.toString(),
                })),
            ],
            codigosObra: [
                { value: "all", label: "Todos los Códigos de obra" },
                ...codigosUnicos.map((c) => ({
                    value: c,
                    label: `COD-${c}`,
                })),
            ],
            residuosDisponibles: residuosUnicos.map((r) => ({
                value: r,
                label: r,
            })),

            // 🔥 NUEVOS DROPDOWNS
            transportistas: [
                { value: "all", label: "Todos los transportistas" },
                ...transportistasUnicos.map((t) => ({
                    value: t,
                    label: t,
                })),
            ],
            gestoresFinales: [
                { value: "all", label: "Todos los gestores" },
                ...gestoresFinalesUnicos.map((g) => ({
                    value: g,
                    label: g,
                })),
            ],
        };
    }, [datosNormalizados]);

    const datosFiltrados = useMemo(() => {
        return datosNormalizados.filter((item) => {
            return (
                (selectedProyecto === "all" ||
                    item.proyectoFinal === selectedProyecto) &&
                (selectedFecha === "all" ||
                    item.anioResiduo === Number(selectedFecha)) &&
                (selectedCodigo === "all" ||
                    item.codObra === Number(selectedCodigo)) &&
                (selectedTransportista === "all" ||
                    item.transportista === selectedTransportista) &&
                (selectedGestorFinal === "all" ||
                    item.gestorFinal === selectedGestorFinal) &&
                (selectedMes === "all" || 
                    item.mesResiduo === Number(selectedMes))
            );
        });
    }, [
        datosNormalizados,
        selectedProyecto,
        selectedFecha,
        selectedCodigo,
        selectedTransportista,
        selectedGestorFinal,
        selectedMes,
    ]);

    const {
        datosTabla,
        datosBarChart,
        datosPieChart,
        totalCantidadGenerada,
        totalValorizacion,
        totalEliminacion,
        porcentajeValorizacion,
        porcentajeEliminacion,
    } = useMemo(() => {
        const datosAgrupadosTabla = Object.values(
            datosFiltrados.reduce((acc, item) => {
                if (!acc[item.residuo]) {
                    acc[item.residuo] = {
                        residuo: item.residuo,
                        pesoTotal: 0,
                        cantidadValorizada: 0,
                        cantidadEliminada: 0,
                        volumenTotal: 0,
                    };
                }
                acc[item.residuo].pesoTotal += item.pesoTotal;
                acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
                acc[item.residuo].cantidadEliminada += item.cantidadEliminada;
                acc[item.residuo].volumenTotal += item.volumenTotal;
                return acc;
            }, {})
        );

        const residuosSeleccionados =
            selectedResiduos.length > 0
                ? selectedResiduos.map((r) => r.value)
                : [];

        const datosFiltradosPorResiduos =
            residuosSeleccionados.length > 0
                ? datosFiltrados.filter((item) =>
                      residuosSeleccionados.includes(item.residuo)
                  )
                : datosFiltrados;

        const agregadosBar = datosFiltradosPorResiduos.reduce((acc, item) => {
            if (!acc[item.residuo]) {
                acc[item.residuo] = {
                    cantidadEliminada: 0,
                    cantidadValorizada: 0,
                };
            }
            acc[item.residuo].cantidadEliminada += item.cantidadEliminada;
            acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
            return acc;
        }, {});

        const datosProcesadosBar = Object.keys(agregadosBar)
            .map((residuo) => ({
                name: residuo,
                Eliminación: agregadosBar[residuo].cantidadEliminada,
                Valorización: agregadosBar[residuo].cantidadValorizada,
            }))
            .sort(
                (a, b) =>
                    b.Eliminación +
                    b.Valorización -
                    (a.Eliminación + a.Valorización)
            );

        const totalVal = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadValorizada,
            0
        );
        const totalElim = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadEliminada,
            0
        );
        const granTotal = totalVal + totalElim;
        const porcVal = granTotal > 0 ? (totalVal / granTotal) * 100 : 0;
        const porcElim = granTotal > 0 ? (totalElim / granTotal) * 100 : 0;
        const datosProcesadosPie = [
            { name: "Valorización", value: parseFloat(porcVal.toFixed(2)) },
            { name: "Eliminación", value: parseFloat(porcElim.toFixed(2)) },
        ];

        const totalGenerado = datosFiltrados.reduce(
            (sum, item) => sum + item.pesoTotal,
            0
        );

        return {
            datosTabla: datosAgrupadosTabla,
            datosBarChart: datosProcesadosBar,
            datosPieChart: datosProcesadosPie,
            totalCantidadGenerada: totalGenerado,
            totalValorizacion: totalVal,
            totalEliminacion: totalElim,
            porcentajeValorizacion: porcVal.toFixed(1),
            porcentajeEliminacion: porcElim.toFixed(1),
        };
    }, [datosFiltrados, selectedResiduos]);

    const formatKg = (value) => new Intl.NumberFormat("es-ES").format(value);

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    const proyectoValue = proyectos.find((p) => p.value === selectedProyecto);
    const fechaValue = fechasFin.find((f) => f.value === selectedFecha);
    const codigoValue = codigosObra.find((c) => c.value === selectedCodigo);
    const mesValue = meses.find((m) => m.value === selectedMes);

    function pad2(n) {
        return String(n).padStart(2, "0");
    }

    function timestampUTCYYYYMMDDHHmmss(date = new Date()) {
        const Y = date.getUTCFullYear();
        const M = pad2(date.getUTCMonth() + 1);
        const D = pad2(date.getUTCDate());
        const h = pad2(date.getUTCHours());
        const m = pad2(date.getUTCMinutes());
        const s = pad2(date.getUTCSeconds());
        return `${Y}${M}${D}${h}${m}${s}`;
    }

    const handleExportToExcel = async (tipo) => {
        const api = getApiInstance();
        const codZona = selectedZone.value;
        const codZonaLegal = selectedZone.value;

        try {
            var apiUrl = "api/Informe/CreateReportExcelCompleto";
            if (tipo === "resumen") {
                apiUrl = "api/Informe/CreateReportExcelCompletoAgrupado";
            }

            const codObra =
                codigoValue && codigoValue.value != "all"
                    ? codigoValue.value
                    : "0";
            const proyectoVal =
                proyectoValue && proyectoValue.value != "all"
                    ? proyectoValue.value
                    : "0";
            const fechaVal =
                fechaValue && fechaValue.value != "all"
                    ? fechaValue.value
                    : "0";
            const selectedResiduosValues =
                selectedResiduos.length > 0 ? selectedResiduos[0].value : 0;

            const query = new URLSearchParams({
                CodObra: codObra,
                CodProyecto: proyectoVal,
                codFecha: fechaVal,
                CodTipoObra: selectedResiduosValues,
                codZona: codZona,
                codZonaLegal: codZonaLegal,
            });

            const finalUrl = `${apiUrl}?${query.toString()}`;

            const response = await api.post(finalUrl, null, {
                responseType: "blob",
            });

            const contentDisposition = response.headers["content-disposition"];
            let fileName = `Informe_Cantidades_Obra_${selectedCodigo.value || "0"}__${timestampUTCYYYYMMDDHHmmss()}.xlsx`;
            if (contentDisposition) {
                const fileNameMatch =
                    contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        }
    };

    return (
        <PageLayout
            title="Cantidades generadas por centro de producción"
            subtitle="Análisis detallado de los residuos generados y su tratamiento"
            actions={[
                <Button
                    key="export"
                    componentType="button"
                    variant="outlined"
                    startIcon={<FiDownload />}
                    onClick={() => setIsModalExcelOpen(true)}
                >
                    Exportar a excel
                </Button>,
            ]}
        >
            {error ? (
                <div
                    className="error-message"
                    style={{
                        padding: "40px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#d32f2f",
                    }}
                >
                    {error}
                </div>
            ) : (
                <>
                    <TabsNavigation
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />

                    {loading ? (
                        <div className="loading-message">Cargando datos...</div>
                    ) : activeTab === "grafica" ? (
                        <div className="cantidades-generadas">
                            <div className="filters-section">
                                <div className="filters-section__header">
                                    <div className="filters-section__icon">
                                        <MdFilterList />
                                    </div>
                                    <h3 className="filters-section__title">
                                        Filtrar datos
                                    </h3>
                                </div>
                                <div className="filters-section__grid">
                                    <InputDropdown
                                        value={proyectoValue}
                                        options={proyectos}
                                        onChange={(option) =>
                                            setSelectedProyecto(option.value)
                                        }
                                        placeholder="Seleccionar proyecto"
                                        labelVisible={false}
                                    />
                                    <InputDropdown
                                        value={transportistas.find(
                                            (t) =>
                                                t.value ===
                                                selectedTransportista
                                        )}
                                        options={transportistas}
                                        onChange={(option) =>
                                            setSelectedTransportista(
                                                option.value
                                            )
                                        }
                                        placeholder="Seleccionar transportista"
                                        labelVisible={false}
                                    />

                                    <InputDropdown
                                        value={gestoresFinales.find(
                                            (g) =>
                                                g.value === selectedGestorFinal
                                        )}
                                        options={gestoresFinales}
                                        onChange={(option) =>
                                            setSelectedGestorFinal(option.value)
                                        }
                                        placeholder="Seleccionar gestor final"
                                        labelVisible={false}
                                    />
                                    <InputDropdown
                                        value={fechaValue}
                                        options={fechasFin}
                                        onChange={(option) =>
                                            setSelectedFecha(option.value)
                                        }
                                        placeholder="Seleccionar fecha"
                                        labelVisible={false}
                                    />
                                    <InputDropdown
                                        value={mesValue}
                                        options={meses}
                                        onChange={(option) =>
                                            setSelectedMes(option.value)
                                        }
                                        placeholder="Seleccionar mes"
                                        labelVisible={false}
                                    />
                                    {/* <InputDropdown
                                        value={codigoValue}
                                        options={codigosObra}
                                        onChange={(option) =>
                                            setSelectedCodigo(option.value)
                                        }
                                        placeholder="Seleccionar código"
                                        labelVisible={false}
                                    /> */}
                                </div>
                            </div>

                            <div className="stats-grid">
                                <CardStats
                                    title="Total Generado"
                                    subtitle="Cantidad total de residuos"
                                    icon={HiTrendingUp}
                                    variant="primary"
                                    showTabs={false}
                                    showTrend={false}
                                    data={{
                                        metrics: {
                                            Semana: {
                                                amount: `${formatKg(totalCantidadGenerada)} kg`,
                                                percentageValue:
                                                    porcentajeValorizacion,
                                                trendDirection: "up",
                                            },
                                        },
                                    }}
                                    percentage={false}
                                />

                                <CardStats
                                    title="Valorización"
                                    subtitle="Residuos valorizados"
                                    icon={MdRecycling}
                                    variant="success"
                                    showTabs={false}
                                    showTrend={false}
                                    data={{
                                        metrics: {
                                            Semana: {
                                                amount: `${porcentajeValorizacion}%`,
                                            },
                                        },
                                    }}
                                    percentage={false}
                                />

                                <CardStats
                                    title="Eliminación"
                                    subtitle="Residuos eliminados"
                                    icon={MdDelete}
                                    variant="danger"
                                    showTabs={false}
                                    showTrend={false}
                                    data={{
                                        metrics: {
                                            Semana: {
                                                amount: `${porcentajeEliminacion}%`,
                                            },
                                        },
                                    }}
                                    percentage={false}
                                />
                            </div>

                            <div className="charts-grid">
                                <div className="chart-card">
                                    <div className="chart-card__header">
                                        <div>
                                            <h3 className="chart-card__title">
                                                Cantidad generada por Residuo
                                            </h3>
                                            <p className="chart-card__subtitle">
                                                Distribución de residuos por
                                                tipo y tratamiento
                                            </p>
                                        </div>
                                    </div>

                                    <div className="chart-card__filter">
                                        <InputDropdown
                                            value={selectedResiduos}
                                            options={residuosDisponibles}
                                            onChange={(selectedOptions) => {
                                                setSelectedResiduos(
                                                    Array.isArray(
                                                        selectedOptions
                                                    )
                                                        ? selectedOptions
                                                        : []
                                                );
                                            }}
                                            placeholder="Filtrar por tipo de residuo"
                                            labelVisible={false}
                                            isMultiple={true}
                                            clearable={true}
                                        />
                                    </div>

                                    <div className="chart-card__body">
                                        <ScrollableBarChartGraph
                                            data={datosBarChart}
                                        />
                                    </div>
                                </div>

                                <div className="chart-card chart-card--compact">
                                    <div className="chart-card__header">
                                        <h3 className="chart-card__title">
                                            Distribución Global
                                        </h3>
                                    </div>

                                    <div className="chart-card__body">
                                        <PieChartGraph
                                            data={{ all: datosPieChart }}
                                            activeTab="all"
                                        />
                                    </div>

                                    <div className="chart-card__legend">
                                        <div className="legend-item legend-item--success">
                                            <div className="legend-item__dot"></div>
                                            <div className="legend-item__content">
                                                <span className="legend-item__label">
                                                    Valorización
                                                </span>
                                                <span className="legend-item__value">
                                                    {formatKg(
                                                        totalValorizacion
                                                    )}{" "}
                                                    kg
                                                </span>
                                            </div>
                                        </div>
                                        <div className="legend-item legend-item--danger">
                                            <div className="legend-item__dot"></div>
                                            <div className="legend-item__content">
                                                <span className="legend-item__label">
                                                    Eliminación
                                                </span>
                                                <span className="legend-item__value">
                                                    {formatKg(totalEliminacion)}{" "}
                                                    kg
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cantidades-generadas">
                            <div className="filters-section">
                                <div className="filters-section__header">
                                    <div className="filters-section__icon">
                                        <MdFilterList />
                                    </div>
                                    <h3 className="filters-section__title">
                                        Filtrar datos
                                    </h3>
                                </div>
                                <div className="filters-section__grid">
                                    <InputDropdown
                                        value={proyectoValue}
                                        options={proyectos}
                                        onChange={(option) =>
                                            setSelectedProyecto(option.value)
                                        }
                                        placeholder="Seleccionar proyecto"
                                        labelVisible={false}
                                    />
                                    <InputDropdown
                                        value={transportistas.find(
                                            (t) =>
                                                t.value ===
                                                selectedTransportista
                                        )}
                                        options={transportistas}
                                        onChange={(option) =>
                                            setSelectedTransportista(
                                                option.value
                                            )
                                        }
                                        placeholder="Seleccionar transportista"
                                        labelVisible={false}
                                    />

                                    <InputDropdown
                                        value={gestoresFinales.find(
                                            (g) =>
                                                g.value === selectedGestorFinal
                                        )}
                                        options={gestoresFinales}
                                        onChange={(option) =>
                                            setSelectedGestorFinal(option.value)
                                        }
                                        placeholder="Seleccionar gestor final"
                                        labelVisible={false}
                                    />
                                    {/* <InputDropdown
                                        value={anos.find(
                                            (g) => g.value === selectedAnos
                                        )}
                                        options={anos}
                                        onChange={(option) =>
                                            setSelectedAnos(option.value)
                                        }
                                        placeholder="Seleccionar año"
                                        labelVisible={false}
                                    /> */}
                                    <InputDropdown
                                        value={fechaValue}
                                        options={fechasFin}
                                        onChange={(option) =>
                                            setSelectedFecha(option.value)
                                        }
                                        placeholder="Seleccionar fecha"
                                        labelVisible={false}
                                    />
                                    <InputDropdown
                                        value={mesValue}
                                        options={meses}
                                        onChange={(option) =>
                                            setSelectedMes(option.value)
                                        }
                                        placeholder="Seleccionar mes"
                                        labelVisible={false}
                                    />
                                    {/* <InputDropdown
                                        value={codigoValue}
                                        options={codigosObra}
                                        onChange={(option) =>
                                            setSelectedCodigo(option.value)
                                        }
                                        placeholder="Seleccionar código"
                                        labelVisible={false}
                                    /> */}
                                </div>
                            </div>

                            <div className="table-wrapper">
                                <Table
                                    columns={columnasResiduos}
                                    dataTable={datosTabla}
                                    keyProp="residuo"
                                    sortProp="residuo"
                                    showCheckboxes={false}
                                    noResultsMessage="No se han encontrado residuos con los filtros aplicados."
                                    elementName="tipo de residuo"
                                    loading={loading}
                                    rowsPerPage={10}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
            <Modal
                isOpen={isModalExcelOpen}
                onClose={() => setIsModalExcelOpen(false)}
                title="Exportar informe a Excel"
                actions={[
                    <Button
                        className="export-excel__btn export-excel__btn--primary"
                        onClick={() => handleExportToExcel("resumen")}
                        startIcon={<MdOutlineFileDownload />}
                    >
                        Informe Resumen
                    </Button>,
                    <Button
                        className="export-excel__btn export-excel__btn--secondary"
                        onClick={() => handleExportToExcel("completo")}
                        startIcon={<MdOutlineFileDownload />}
                    >
                        Informe Completo
                    </Button>,
                ]}
            >
                <div className="export-excel__actions"></div>
            </Modal>
        </PageLayout>
    );
}
